import {
  ARTIFACTS_PATH,
  AssetTypeSchema,
  DeviceSlotSchema,
  EXPERIMENT_RESOURCE,
  INVENTORY_SCAN_RESOURCE,
  isJobActive,
  LibraryRoleSchema,
  LISTING_SNAPSHOT_LABEL,
  OUTCOME_RESOURCE,
  type JobState,
  type AppProfileResponse,
  type AppResource,
  type AssetType,
  type DeviceSlot,
  type ExperimentResource,
  type LibraryItemResource,
  type ListingAssetRole,
  type ListingSnapshotResource,
  type StudioEvent,
  type StoreDesignResource,
} from '@studio/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '../shell/api.js';
import { EmptyState, Failure } from '../shell/EmptyState.js';
import { Link, useNavigate, type Surface } from '../shell/router.js';
import { useResourceEvents } from '../shell/stream.js';
import { useAsync } from '../shell/useAsync.js';
import { LearningShelf } from '../experiment/learn/Shelf.js';
import {
  assetTypeLabel,
  chronological,
  deviceSlotLabel,
  matches,
  NO_FILTER,
  OUTCOME_LABELS,
  outcomeLine,
  pageOf,
  targetKey,
  targetLabel,
  type HistoryFilter,
  type OutcomeFilter,
} from './history.js';
import { AppArchive } from './Archive.js';
import { materialName, retrievalNote } from './material.js';
import { StoreDesignShelf } from '../store-design/surface.js';

interface AppView {
  readonly profile: AppProfileResponse;
  readonly experiments: readonly ExperimentResource[];
  readonly designs: readonly StoreDesignResource[];
}

/**
 * R025. What this page shows, changing anywhere but here.
 *
 * The page leads with the Experiments of one App and it held one subscription, to
 * `outcome`: the card of a closed Experiment moved to its verdict, and nothing else on
 * the page ever moved at all. An Experiment started at the next desk, a Hypothesis or a
 * Target written into one, a state that turned over - none of it arrived until somebody
 * thought to reload. A page that shows Experiments has to hear about Experiments.
 *
 * A `StudioEvent` carries no `appId`, so the Experiments of every other App in the
 * Workspace arrive here too, and re-reading on each of them would make an ordinary
 * spread next door re-read this page a hundred times. The snapshot already on screen is
 * what separates them:
 *
 * - an id the snapshot knows is this App's, and only a version newer than the one on
 *   screen is news - a duplicate or an event this page has already answered is not;
 * - an id it does not know is either an Experiment just started here or one belonging
 *   to another App, and nothing but a read can tell the two apart. So it is read for
 *   exactly once: an id that turns out to be ours joins the snapshot and is answered by
 *   version from then on, and an id that does not is remembered here and never read for
 *   again.
 *
 * `outcome` stays its own case. It is the resource that closes an Experiment, it is
 * recorded on the Learn stage, and it is what moves a card into the history below.
 */
function useAppEvents(
  appId: string,
  experiments: readonly ExperimentResource[],
  reload: () => void,
): (event: StudioEvent) => void {
  /** Experiment ids this page has already gone and read for, and did not find. */
  const asked = useRef<Set<string>>(new Set());
  // What was foreign to one App says nothing about the next one, and an id read for
  // while another App was open must not silence the first news of it on this one.
  useEffect(() => {
    asked.current = new Set();
  }, [appId]);

  return useCallback(
    (event: StudioEvent) => {
      if (event.resource === OUTCOME_RESOURCE || event.resource === 'storeDesign') {
        reload();
        return;
      }
      if (event.resource !== EXPERIMENT_RESOURCE) return;
      const standing = experiments.find((one) => one.id === event.id);
      if (standing !== undefined) {
        if (event.version > standing.version) reload();
        return;
      }
      if (asked.current.has(event.id)) return;
      asked.current.add(event.id);
      reload();
    },
    [experiments, reload],
  );
}

function AppPage({ appId }: { appId: string }): ReactNode {
  const loaded = useAsync<AppView>(async () => {
    const [profile, experiments, designs] = await Promise.all([api.readAppProfile(appId), api.listExperiments(appId), api.listStoreDesigns(appId)]);
    const grouped = new Set(designs.designs.flatMap((design) => [design.iconExperimentId, design.screenshotsExperimentId]));
    return { profile, experiments: experiments.experiments.filter((experiment) => !grouped.has(experiment.id)), designs: designs.designs };
  }, [appId]);
  const { reload } = loaded;

  const [filter, setFilter] = useState<HistoryFilter>(NO_FILTER);
  const [pages, setPages] = useState(1);

  const value = loaded.value;
  const active = useMemo(
    () => (value === null ? [] : chronological(value.experiments.filter((one) => one.state !== 'closed'))),
    [value],
  );
  const history = useMemo(
    () =>
      value === null
        ? []
        : chronological(value.experiments.filter((one) => one.state === 'closed')).filter((one) => matches(one, filter)),
    [value, filter],
  );

  useResourceEvents(useAppEvents(appId, value?.experiments ?? [], reload));

  if (loaded.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">App</p>
        <Failure message={loaded.error} onRetry={reload} />
      </section>
    );
  }
  if (value === null) return <section className="page" aria-busy="true" />;

  const app = value.profile.app;
  const control = value.profile.latestSnapshot?.assets.find((asset) => asset.artifactId !== null) ?? null;
  const shown = pageOf(history, pages);

  return (
    <section className="page">
      <header className="app-hero">
        <div>
          <p className="eyebrow">{`App · ${app.packageName ?? 'не опубликовано в Google Play'}`}</p>
          <h1>{app.displayName}</h1>
          <p className="note">
            {`${targetLabel(app.defaultTarget)} — постоянный Target${
              value.profile.latestSnapshot === null ? '' : ` · Listing Snapshot — ${LISTING_SNAPSHOT_LABEL}`
            }`}
          </p>
          <AppArchive app={app} onChanged={reload} />
        </div>
        <StartExperiment appId={appId} />
      </header>

      <div className="app-layout">
        <main>
          <StoreDesignShelf appId={appId} designs={value.designs} />
          <h2>Активные Experiments</h2>
          {active.length === 0 ? (
            <EmptyState
              title="На этом App ещё нет Experiments."
              note="Выберите один Asset Type выше — и сразу начинается первый spread из шести. Больше ничего не нужно."
            />
          ) : (
            <ul className="experiment-list" aria-label="Активные Experiments">
              {active.map((experiment) => (
                <li key={experiment.id}>
                  <Link to={`/experiments/${experiment.id}/brief`} className="experiment-row">
                    <span className="experiment-headline">{experiment.hypothesis ?? 'Hypothesis ещё нет'}</span>
                    <span className="note">
                      {`${assetTypeLabel(experiment.assetType)} · ${targetLabel(experiment.target)} · ${experiment.state}`}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <h2>Закрытые Experiments</h2>
          <HistoryFilters
            experiments={value.experiments.filter((one) => one.state === 'closed')}
            filter={filter}
            onChange={(next) => {
              setFilter(next);
              setPages(1);
            }}
          />
          {history.length === 0 ? (
            <EmptyState
              title="Здесь ещё ничего не закрыто."
              note="Закрытый Experiment попадает сюда со своим Control, Candidate и тем Outcome, который получился."
            />
          ) : (
            <>
              <ul className="history-gallery" aria-label="Закрытые Experiments">
                {shown.map((experiment) => (
                  <li key={experiment.id} className="history-card">
                    <div className="history-visuals">
                      {control?.artifactId === undefined || control.artifactId === null ? (
                        <span className="frame frame-blank" aria-hidden="true" />
                      ) : (
                        <img
                          className="frame"
                          src={`${ARTIFACTS_PATH}/${control.artifactId}`}
                          alt={`Control-креатив для ${app.displayName}`}
                          loading="lazy"
                        />
                      )}
                      <CandidateVisual experimentId={experiment.id} />
                    </div>
                    <Link to={`/experiments/${experiment.id}/compare`} className="history-headline">
                      {experiment.hypothesis ?? 'Hypothesis не записана'}
                    </Link>
                    <p className="note">
                      {`${assetTypeLabel(experiment.assetType)} · ${targetLabel(experiment.target)}`}
                    </p>
                    {/* R025: the verdict and the one number that answers `by how much`,
                        both read from the list itself rather than said unconditionally. */}
                    <p className="note">{outcomeLine(experiment.outcome)}</p>
                  </li>
                ))}
              </ul>
              {shown.length < history.length ? (
                <button className="ghost" type="button" onClick={() => setPages((n) => n + 1)}>
                  {`Показать ещё закрытые Experiments (осталось ${history.length - shown.length})`}
                </button>
              ) : null}
            </>
          )}
        </main>

        <aside className="learning-shelf">
          <h2>Что работало раньше</h2>
          <LearningShelf appId={appId} />
          <h2>App Profile</h2>
          <ProfileEditor app={app} onSaved={reload} />
          <h2>App Library</h2>
          <LibraryStrip appId={appId} onChanged={reload} />
          <h2>Listing Google Play</h2>
          <ListingSnapshots appId={appId} reference={value.profile.latestSnapshot} onChanged={reload} />
        </aside>
      </div>
    </section>
  );
}

/** The gallery leads with the creative. The list carries no Artifact, so it is read here. */
function CandidateVisual({ experimentId }: { experimentId: string }): ReactNode {
  const loaded = useAsync(async () => api.readExperiment(experimentId), [experimentId]);
  const artifactId = loaded.value?.candidates.find((candidate) => candidate.artifactId !== null)?.artifactId ?? null;
  if (artifactId === null) return <span className="frame frame-blank" aria-hidden="true" />;
  return <img className="frame" src={`${ARTIFACTS_PATH}/${artifactId}`} alt="Candidate-креатив" loading="lazy" />;
}

function HistoryFilters({
  experiments,
  filter,
  onChange,
}: {
  experiments: readonly ExperimentResource[];
  filter: HistoryFilter;
  onChange: (next: HistoryFilter) => void;
}): ReactNode {
  const targets = [...new Map(experiments.map((one) => [targetKey(one.target), one.target])).entries()];
  return (
    <div className="history-filters">
      <label htmlFor="history-search">Поиск по закрытым Experiments</label>
      <input
        id="history-search"
        value={filter.query}
        onChange={(event) => onChange({ ...filter, query: event.target.value })}
      />
      <label htmlFor="history-asset">Asset Type</label>
      <select
        id="history-asset"
        value={filter.assetType}
        onChange={(event) => onChange({ ...filter, assetType: event.target.value as AssetType | 'all' })}
      >
        <option value="all">Все</option>
        {AssetTypeSchema.options.map((option) => (
          <option key={option} value={option}>
            {assetTypeLabel(option)}
          </option>
        ))}
      </select>
      <label htmlFor="history-target">Target</label>
      <select
        id="history-target"
        value={filter.target}
        onChange={(event) => onChange({ ...filter, target: event.target.value })}
      >
        <option value="all">Все</option>
        {targets.map(([key, target]) => (
          <option key={key} value={key}>
            {targetLabel(target)}
          </option>
        ))}
      </select>
      {/* R026: the third filter. `Not recorded yet` is one of its answers, because a
          closed Experiment nobody wrote an Outcome for is a thing to go looking for. */}
      <label htmlFor="history-outcome">Outcome</label>
      <select
        id="history-outcome"
        value={filter.outcome}
        onChange={(event) => onChange({ ...filter, outcome: event.target.value as OutcomeFilter })}
      >
        <option value="all">Все</option>
        {(Object.keys(OUTCOME_LABELS) as (keyof typeof OUTCOME_LABELS)[]).map((verdict) => (
          <option key={verdict} value={verdict}>
            {OUTCOME_LABELS[verdict]}
          </option>
        ))}
        <option value="notRecorded">Ещё не записан</option>
      </select>
    </div>
  );
}

/**
 * The standing half of an App Profile: the constraints and the positioning every
 * Experiment of this App inherits. They are typed where they are read and saved as they
 * are left, so nothing here is a settings screen with a Save button of its own.
 */
function ProfileEditor({ app, onSaved }: { app: AppResource; onSaved: () => void }): ReactNode {
  const [failure, setFailure] = useState<string | null>(null);

  const save = (patch: { standingConstraints?: string; positioning?: string }): void => {
    setFailure(null);
    void api
      .updateAppProfile(app.id, patch)
      .then(onSaved)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось сохранить.'));
  };

  return (
    <div className="app-profile">
      <label htmlFor="app-standing-constraints">Постоянные ограничения</label>
      <textarea
        id="app-standing-constraints"
        rows={3}
        key={`constraints-${app.version}`}
        defaultValue={app.standingConstraints}
        placeholder="Никогда не показывать рамку устройства. Wordmark не менять."
        onBlur={(event) => {
          if (event.target.value !== app.standingConstraints) save({ standingConstraints: event.target.value });
        }}
      />
      <label htmlFor="app-positioning">Позиционирование</label>
      <textarea
        id="app-positioning"
        rows={2}
        key={`positioning-${app.version}`}
        defaultValue={app.positioning}
        onBlur={(event) => {
          if (event.target.value !== app.positioning) save({ positioning: event.target.value });
        }}
      />
      {failure === null ? null : <Failure message={failure} />}
    </div>
  );
}

/**
 * The App Library, with the two things about a piece of material the operator owns.
 *
 * The role is a guess the Studio made from the bytes and where they came from, so it is
 * shown as a guess and corrected in place. Standing is the other half: material that is
 * part of this App rather than of one Experiment. Neither is selection - membership
 * stays passive until a Brief names the piece.
 */
function LibraryStrip({ appId, onChanged }: { appId: string; onChanged: () => void }): ReactNode {
  const loaded = useAsync(async () => api.readLibrary(appId), [appId]);
  const { reload } = loaded;
  const [failure, setFailure] = useState<string | null>(null);
  const items = loaded.value?.items ?? [];

  // The ingest Job publishes the item it settled, so the item is the whole signal here.
  // Watching Jobs finish instead was a way around a worker that announced nothing, and
  // it made every unrelated Job in the Workspace re-read this strip.
  useResourceEvents((event) => {
    if (event.resource === 'libraryItem') reload();
  });

  const act = (run: () => Promise<unknown>): void => {
    setFailure(null);
    void run()
      .then(() => {
        reload();
        onChanged();
      })
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось сохранить.'));
  };

  if (items.length === 0) {
    return (
      <EmptyState
        title="Пока ничего не запомнено."
        note="Всё, что попало в Brief, запоминается здесь для этого App и потом можно выбрать снова."
      />
    );
  }
  return (
    <>
      {failure === null ? null : <Failure message={failure} />}
      <ul className="library-strip" aria-label="App Library">
        {items.slice(0, 8).map((item) => {
          const name = materialName(item);
          const retrieval = retrievalNote(item);
          return (
            <li key={item.id}>
              {item.artifactId === null ? (
                <span className="frame frame-blank" aria-hidden="true" />
              ) : (
                <img className="frame" src={`${ARTIFACTS_PATH}/${item.artifactId}`} alt={name} loading="lazy" />
              )}
              {retrieval === null ? null : <p className="note">{retrieval}</p>}
              {item.sourceUrl === null ? null : <p className="note">{item.sourceUrl}</p>}
              <label htmlFor={`library-role-${item.id}`} className="note">
                {`Роль для ${name}`}
              </label>
              <select
                id={`library-role-${item.id}`}
                value={item.role}
                onChange={(event) => act(() => api.setLibraryRole(item.id, event.target.value as LibraryItemResource['role']))}
              >
                {LibraryRoleSchema.options.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              {item.roleInferred ? <span className="note">Определено автоматически. Исправьте, если догадка неверна.</span> : null}
              <button
                type="button"
                className="ghost"
                onClick={() =>
                  act(() => (item.standing ? api.clearStanding(appId, item.id) : api.markStanding(appId, item.id)))
                }
              >
                {item.standing ? `Перестать держать ${name} в App Profile` : `Keep ${name} в App Profile`}
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** One entry of the listing as the review is building it, before anything is written. */
interface ReviewEntry {
  readonly key: string;
  readonly role: ListingAssetRole;
  readonly sourceAssetId?: string;
  /** What the import captured, kept apart so a replacement can be taken back. */
  readonly importedArtifactId: string | null;
  /** The operator's own material, when the imported asset is replaced or one is added. */
  readonly artifactId?: string;
  /**
   * Where the imported trailer lives. A preview video is only ever an address - the
   * bytes stay on YouTube - so this is the whole asset, not a note beside it.
   */
  readonly sourceUrl: string | null;
  /** The operator's replacement address for the trailer. */
  readonly replacementUrl?: string;
}

function entriesOf(snapshot: ListingSnapshotResource): readonly ReviewEntry[] {
  return [...snapshot.assets]
    .sort((left, right) => left.position - right.position)
    .map((asset) => ({
      key: asset.id,
      role: asset.role,
      sourceAssetId: asset.id,
      importedArtifactId: asset.artifactId,
      sourceUrl: asset.sourceUrl,
    }));
}

function moved<T>(items: readonly T[], from: number, to: number): readonly T[] {
  if (to < 0 || to >= items.length) return items;
  const taken = items[from];
  if (taken === undefined) return items;
  const rest = items.filter((_entry, index) => index !== from);
  return [...rest.slice(0, to), taken, ...rest.slice(to)];
}

/**
 * The Listing Snapshots this App's Experiments start from, and the review that corrects one.
 *
 * A scan reads the storefront, never Play Console truth: the device, an audience or a
 * live experiment can all make it show something other than what this App is compared
 * against. So the operator can leave an asset out, put the rest in the right order,
 * replace one with material of their own, or add one the page did not show. Nothing is
 * edited in place - the review writes a new snapshot beside the one it corrects.
 *
 * A scan writes a reading for every Market Locale; those are read in the Inventory. Here
 * are only the ones an Experiment actually starts from and what was corrected by hand.
 */
function ListingSnapshots({
  appId,
  reference,
  onChanged,
}: {
  appId: string;
  reference: ListingSnapshotResource | null;
  onChanged: () => void;
}): ReactNode {
  const loaded = useAsync(async () => api.readSnapshots(appId), [appId]);
  const library = useAsync(async () => api.readLibrary(appId), [appId]);
  const { reload } = loaded;
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [entries, setEntries] = useState<readonly ReviewEntry[]>([]);
  const [title, setTitle] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The scan Job this page is waiting on.
   *
   * Reading a Play listing takes minutes, and for all of them a strip saying `No
   * Listing Snapshot yet.` would read exactly like a scan that never started or one that
   * failed. The press returns the Job it queued, so the page can say which is true.
   */
  const [scanJobId, setScanJobId] = useState<string | null>(null);

  // The list holds the latest readings only, and the reference - usually the first one a
  // scan reads - can be older than all of them. It leads regardless.
  const referenceId = reference?.id ?? null;
  const snapshots = [
    ...(reference === null ? [] : [reference]),
    ...(loaded.value?.snapshots ?? []).filter(
      (snapshot) => snapshot.id !== referenceId && (snapshot.reviewedAt !== null || snapshot.market === null),
    ),
  ];
  const latest = reference ?? snapshots[0] ?? null;
  const usable = (library.value?.items ?? []).filter((item) => item.artifactId !== null);

  // The import is Job work, so the snapshot exists some time after the press and often
  // in a tab nobody is holding. Re-reading the moment the POST returned answered the
  // wrong question - it asked before the worker had written anything - and left the
  // strip on whatever was already there until somebody reloaded the page.
  useResourceEvents((event) => {
    if (event.resource === 'job' && event.id === scanJobId && event.state !== null && !isJobActive(event.state as JobState)) {
      // A scan that ended must not leave the page saying it is still reading: the
      // operator would be watching a sentence that will never change.
      setScanJobId(null);
      reload();
      onChanged();
      return;
    }
    // A scan started anywhere - the App's creation, another tab, `Scan all` - ends here too.
    const scanEnded = event.resource === INVENTORY_SCAN_RESOURCE && event.state !== 'reading';
    if (event.resource !== 'listingSnapshot' && !scanEnded) return;
    reload();
    onChanged();
  });

  // The Inventory sends the operator here to correct one reading of any Store Listing
  // in any language; the review is the same one, opened on that snapshot.
  const asked = useRef<string | null>(null);
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('review');
    if (wanted === null || asked.current === wanted) return;
    asked.current = wanted;
    openReview(wanted);
  }, []);

  const scanAgain = (): void => {
    setFailure(null);
    setBusy(true);
    void api
      .startScan(appId)
      .then((queued) => setScanJobId(queued.scanJobIds[0] ?? null))
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось прочитать этот listing.'))
      .finally(() => setBusy(false));
  };

  const openReview = (snapshotId: string): void => {
    setFailure(null);
    void api
      .readSnapshot(snapshotId)
      .then((snapshot) => {
        setEntries(entriesOf(snapshot));
        setTitle(snapshot.title ?? '');
        setReviewing(snapshotId);
      })
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось прочитать этот snapshot.'));
  };

  const save = (): void => {
    if (reviewing === null) return;
    setBusy(true);
    setFailure(null);
    void api
      .reviewSnapshot(reviewing, {
        assets: entries.map((entry) => ({
          role: entry.role,
          ...(entry.sourceAssetId === undefined ? {} : { sourceAssetId: entry.sourceAssetId }),
          ...(entry.artifactId === undefined ? {} : { artifactId: entry.artifactId }),
          ...(entry.replacementUrl === undefined ? {} : { sourceUrl: entry.replacementUrl }),
        })),
        ...(title === '' ? {} : { title }),
      })
      .then(() => {
        setReviewing(null);
        reload();
        onChanged();
      })
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось сохранить эту правку.'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="snapshot-review">
      {failure === null ? null : <Failure message={failure} />}
      <div className="row-actions">
        <button type="button" className="ghost" disabled={busy || scanJobId !== null} onClick={scanAgain}>
          Сканировать Google Play снова
        </button>
        <Link to="/inventory">Все страны в Inventory</Link>
      </div>

      {scanJobId !== null && snapshots.length === 0 ? (
        <EmptyState
          title="Сканирую Google Play…"
          note={`Каждая страна читается по очереди — это занимает несколько минут. ${LISTING_SNAPSHOT_LABEL} появится здесь сам, когда будет прочитан язык по умолчанию.`}
        />
      ) : snapshots.length === 0 ? (
        <EmptyState
          title="Listing Snapshot ещё нет."
          note={`Сканирование Google Play сохраняет ${LISTING_SNAPSHOT_LABEL} того, что live в каждой стране — с этим сравнивается Candidate.`}
        />
      ) : (
        <ul className="snapshot-strip" aria-label="Listing Snapshots">
          {snapshots.slice(0, 4).map((snapshot) => (
            <li key={snapshot.id}>
              <span>{snapshot.title ?? snapshot.packageName ?? 'Listing'}</span>
              <span className="note">
                {`${snapshot.label} · ${snapshot.market === null ? 'не прочитано' : `${snapshot.market} · ${snapshot.locale ?? ''}`} · ${snapshot.capturedAt.slice(0, 10)}${
                  snapshot.reviewedAt === null ? '' : ' · проверено'
                }`}
              </span>
            </li>
          ))}
        </ul>
      )}

      {latest === null || reviewing !== null ? null : (
        <button type="button" className="secondary" onClick={() => openReview(latest.id)}>
          Проверить listing snapshot
        </button>
      )}

      {reviewing === null ? null : (
        <div className="snapshot-editor">
          <p className="note">
            Это {LISTING_SNAPSHOT_LABEL}, не живая синхронизация. Уберите то, что страница показала по ошибке,
            расставьте остальное в порядке listing и замените или добавьте из материала этого App.
          </p>
          <label htmlFor="snapshot-title">Заголовок listing</label>
          <input id="snapshot-title" value={title} onChange={(event) => setTitle(event.target.value)} />

          <ol className="snapshot-assets" aria-label="Импортированные assets listing">
            {entries.map((entry, index) => {
              // The video is one fixed slot on the listing, not the nth screenshot: no
              // ordinal, no reordering, and its content is the YouTube address itself.
              const name = entry.role === 'previewVideo' ? 'Preview video' : `${entry.role} ${index + 1}`;
              const shownArtifactId = entry.artifactId ?? entry.importedArtifactId;
              const shownUrl = entry.replacementUrl ?? entry.sourceUrl;
              return (
                <li key={entry.key}>
                  {entry.role === 'previewVideo' ? null : shownArtifactId === null ? (
                    <span className="frame frame-blank" aria-hidden="true" />
                  ) : (
                    <img className="frame" src={`${ARTIFACTS_PATH}/${shownArtifactId}`} alt={name} loading="lazy" />
                  )}
                  <div className="snapshot-asset-body">
                    <span className="snapshot-asset-name">{name}</span>
                    {entry.role !== 'previewVideo' ? null : shownUrl === null ? (
                      <span className="note">Страница не показала адрес трейлера.</span>
                    ) : (
                      <a href={shownUrl} target="_blank" rel="noreferrer">
                        {shownUrl}
                      </a>
                    )}
                    <div className="row-actions">
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => setEntries((current) => current.filter((_one, at) => at !== index))}
                      >
                        {`Убрать ${name}`}
                      </button>
                      {entry.role === 'previewVideo' ? null : (
                        <>
                          <button
                            type="button"
                            className="ghost"
                            disabled={index === 0}
                            onClick={() => setEntries((current) => moved(current, index, index - 1))}
                          >
                            {`Сдвинуть ${name} раньше`}
                          </button>
                          <button
                            type="button"
                            className="ghost"
                            disabled={index === entries.length - 1}
                            onClick={() => setEntries((current) => moved(current, index, index + 1))}
                          >
                            {`Сдвинуть ${name} позже`}
                          </button>
                        </>
                      )}
                    </div>
                    <label htmlFor={`snapshot-replace-${entry.key}`} className="note">
                      {`Заменить ${name}`}
                    </label>
                    {entry.role === 'previewVideo' ? (
                      <input
                        id={`snapshot-replace-${entry.key}`}
                        type="url"
                        value={entry.replacementUrl ?? ''}
                        placeholder="https://www.youtube.com/watch?v=…"
                        onChange={(event) => {
                          const value = event.target.value;
                          setEntries((current) =>
                            current.map((one, at) =>
                              at === index ? { ...one, replacementUrl: value === '' ? undefined : value } : one,
                            ),
                          );
                        }}
                      />
                    ) : (
                      <select
                        id={`snapshot-replace-${entry.key}`}
                        value={entry.artifactId ?? ''}
                        onChange={(event) => {
                          const artifactId = event.target.value;
                          setEntries((current) =>
                            current.map((one, at) => {
                              if (at !== index) return one;
                              if (artifactId === '') {
                                return {
                                  key: one.key,
                                  role: one.role,
                                  importedArtifactId: one.importedArtifactId,
                                  sourceUrl: one.sourceUrl,
                                  ...(one.sourceAssetId === undefined ? {} : { sourceAssetId: one.sourceAssetId }),
                                };
                              }
                              return { ...one, artifactId };
                            }),
                          );
                        }}
                      >
                        <option value="">
                          {entry.artifactId === undefined ? 'Keep импортированное' : 'Вернуть импортированное'}
                        </option>
                        {usable.map((item) => (
                          <option key={item.id} value={item.artifactId as string}>
                            {materialName(item)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          <label htmlFor="snapshot-add">Добавить asset listing</label>
          <select
            id="snapshot-add"
            value=""
            onChange={(event) => {
              const artifactId = event.target.value;
              if (artifactId === '') return;
              setEntries((current) => [
                ...current,
                { key: `added-${current.length}-${artifactId}`, role: 'screenshot', importedArtifactId: null, sourceUrl: null, artifactId },
              ]);
            }}
          >
            <option value="">Пока ничего</option>
            {usable.map((item) => (
              <option key={item.id} value={item.artifactId as string}>
                {materialName(item)}
              </option>
            ))}
          </select>

          <div className="row-actions">
            <button type="button" className="primary" disabled={busy} onClick={save}>
              Сохранить проверенный Listing Snapshot
            </button>
            <button type="button" className="ghost" onClick={() => setReviewing(null)}>
              Оставить импорт как есть
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Asset Type is the only required creative choice, so choosing it is what creates the
 * Experiment. There is no form between the choice and the Brief.
 */
function StartExperiment({ appId }: { appId: string }): ReactNode {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<AssetType | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Phone is the ordinary default and every screenshot Experiment stores the slot it
  // was made for, so a Technical Check never has to guess the contract from pixels.
  const [deviceSlot, setDeviceSlot] = useState<DeviceSlot>('phone');

  const start = useCallback(
    async (assetType: AssetType) => {
      setBusy(assetType);
      setError(null);
      try {
        const experiment = await api.createExperiment({
          appId,
          assetType,
          ...(assetType === 'screenshotSet' ? { deviceSlot } : {}),
        });
        navigate(`/experiments/${experiment.id}/brief`);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Не удалось начать Experiment.');
      } finally {
        setBusy(null);
      }
    },
    [appId, deviceSlot, navigate],
  );

  return (
    <div className="start-experiment">
      <p className="eyebrow">Новый Experiment · выберите один Asset Type</p>
      <label htmlFor="new-device-slot">Слот устройства</label>
      <select
        id="new-device-slot"
        value={deviceSlot}
        onChange={(event) => setDeviceSlot(event.target.value as DeviceSlot)}
      >
        {DeviceSlotSchema.options.map((option) => (
          <option key={option} value={option}>
            {deviceSlotLabel(option)}
          </option>
        ))}
      </select>
      <p className="note">Нужен для Experiment типа Screenshot set. У остальных Asset Type слота нет.</p>
      <div className="asset-choices" role="group" aria-label="Новый Experiment">
        {AssetTypeSchema.options.map((option) => (
          <button
            key={option}
            type="button"
            className="asset-choice"
            disabled={busy !== null}
            onClick={() => void start(option)}
          >
            {assetTypeLabel(option)}
          </button>
        ))}
      </div>
      {error === null ? null : (
        <p className="failure" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export const surface: Surface = {
  id: 'app',
  path: '/apps/:appId',
  title: 'App',
  render: (params) => <AppPage appId={params.appId ?? ''} />,
};
